export function deviceMeta() {
  return {
    userAgent: navigator.userAgent,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    tela: `${screen.width}x${screen.height}`,
  };
}

export function capturePhoto(facingMode: 'user' | 'environment', metaLines: string[] = []): Promise<string | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    (input as any).capture = facingMode;
    input.style.display = 'none';
    document.body.appendChild(input);
    let resolved = false;
    let cleanup: () => void = () => {};

    function finish(value: string | null) {
      if (resolved) return;
      resolved = true;
      cleanup();
      if (document.body.contains(input)) document.body.removeChild(input);
      resolve(value);
    }

    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) { finish(null); return; }
      try {
        const dataUrl = await compressAndWatermark(file, metaLines);
        finish(dataUrl);
      } catch {
        finish(null);
      }
    };

    // Detectar "usuário voltou sem tirar foto" (cancelou a câmera/o seletor).
    // `focus` sozinho é conhecido por falhar em vários WebViews Android — por
    // isso também escutamos `visibilitychange`, mais confiável nesses casos.
    function onReturn() {
      setTimeout(() => {
        if (!resolved && (!input.files || input.files.length === 0)) finish(null);
      }, 900);
    }
    function onVisibility() {
      if (document.visibilityState === 'visible') onReturn();
    }
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onVisibility);
    cleanup = () => {
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimeout(safetyTimer);
    };

    // Rede de segurança: nunca travar para sempre, mesmo que nenhum dos
    // eventos acima dispare (variação de comportamento entre navegadores).
    const safetyTimer = setTimeout(() => finish(null), 120000);

    input.click();
  });
}

export async function compressAndWatermark(file: File, metaLines: string[], maxWidth = 360, quality = 0.55): Promise<string> {
  let w = 0, h = 0;
  let drawSource: ImageBitmap | HTMLImageElement | null = null;
  try {
    if ('createImageBitmap' in window) {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as any);
      const scale = Math.min(1, maxWidth / bitmap.width);
      w = Math.round(bitmap.width * scale);
      h = Math.round(bitmap.height * scale);
      const small = await createImageBitmap(bitmap, 0, 0, bitmap.width, bitmap.height, {
        resizeWidth: w, resizeHeight: h, resizeQuality: 'medium', imageOrientation: 'from-image',
      } as any);
      bitmap.close?.();
      drawSource = small;
    }
  } catch {
    drawSource = null;
  }
  if (!drawSource) {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target!.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = dataUrl;
    });
    const scale = Math.min(1, maxWidth / img.width);
    w = Math.round(img.width * scale);
    h = Math.round(img.height * scale);
    drawSource = img;
  }
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(drawSource as any, 0, 0, w, h);
  (drawSource as ImageBitmap).close?.();
  if (metaLines.length) {
    const lineH = 11;
    const stripH = metaLines.length * lineH + 8;
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillRect(0, h - stripH, w, stripH);
    ctx.fillStyle = '#fff';
    ctx.font = '9px monospace';
    metaLines.forEach((ln, i) => { ctx.fillText(String(ln).slice(0, 72), 6, h - stripH + 12 + i * lineH); });
  }
  return canvas.toDataURL('image/jpeg', quality);
}

export async function tryGetIP(): Promise<string | null> {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 3000);
    const r = await fetch('https://api.ipify.org?format=json', { signal: ctrl.signal });
    clearTimeout(t);
    const d = await r.json();
    return d.ip || null;
  } catch {
    return null;
  }
}

export function geoPreCheckMessage(): string | null {
  if (!window.isSecureContext) {
    return 'A localização só funciona quando este sistema é aberto por um link seguro (começando com https://). Se você abriu o arquivo diretamente, peça ao gestor o link correto de acesso.';
  }
  if (!navigator.geolocation) {
    return 'Este navegador não oferece suporte a geolocalização.';
  }
  return null;
}

export function geoErrorMessage(err: GeolocationPositionError | null): string {
  if (!err) return 'Não foi possível obter a localização.';
  switch (err.code) {
    case 1: return 'Você negou a permissão de localização para este site. Vá nas configurações do navegador, permita a localização para esta página e tente novamente.';
    case 2: return 'Não foi possível determinar sua localização agora. Verifique se o GPS do aparelho está ativado e se você está com boa recepção, depois tente novamente.';
    case 3: return 'A localização demorou demais para responder. Tente novamente em um local com melhor sinal.';
    default: return err.message || 'Erro desconhecido ao obter a localização.';
  }
}

export async function urlToDataUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export function getCurrentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 12000 });
  });
}
